"use client"

import { Sparkles, Mic, Loader2, Pause } from "lucide-react"
import { Button } from "./button"
import { GenerateButton } from "./generate-button"
import { useVoiceRecording } from "../../hooks/useVoiceRecording"
import { useEffect } from "react"
import styled from "styled-components"
import { motion, AnimatePresence } from "framer-motion"

// Sound wave container for voice recording
const SoundWaveWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  width: 24px;
  height: 20px;
`

// Individual sound bar that responds to audio level
const SoundBar = styled.div`
  width: 3px;
  border-radius: 2px;
  background-color: white;
  transition: height 0.1s ease-out;
  
  &.speaking {
    animation: soundWave 0.3s ease-in-out infinite alternate;
  }
  
  &.speaking.bar-1 {
    animation-delay: 0s;
  }
  
  &.speaking.bar-2 {
    animation-delay: 0.15s;
  }
  
  &.speaking.bar-3 {
    animation-delay: 0.3s;
  }
  
  @keyframes soundWave {
    0% {
      transform: scaleY(1);
    }
    100% {
      transform: scaleY(0.4);
    }
  }
`

export function AIInput({
  value,
  onValueChange,
  onSubmit,
  onVoiceRecordingComplete,
  isLoading = false,
  isTranscribing = false,
  showDisclaimer = false,
  placeholder = 'Try: "I need 400 nails, 10 tons of cement, 20 drywall sheets, and 50 ft of copper piping"',
}) {
  const { 
    isRecording, 
    error: recordingError, 
    audioLevel,
    startRecording, 
    stopRecording,
    clearError 
  } = useVoiceRecording()

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      if (!isLoading && !isTranscribing && !isRecording && value?.trim()) {
        onSubmit?.()
      }
    }
  }

  const handleMicClick = async () => {
    if (isRecording) {
      // Stop recording and get the audio blob
      const audioBlob = await stopRecording()
      if (audioBlob && onVoiceRecordingComplete) {
        onVoiceRecordingComplete(audioBlob)
      }
    } else {
      // Start recording
      await startRecording()
    }
  }

  // Clear recording error after 5 seconds
  useEffect(() => {
    if (recordingError) {
      const timer = setTimeout(() => clearError(), 5000)
      return () => clearTimeout(timer)
    }
  }, [recordingError, clearError])

  // Determine if mic button should be disabled
  const isMicDisabled = isLoading || isTranscribing

  return (
    <div className="w-full max-w-3xl mx-auto space-y-1">
      {/* Recording Error Message */}
      {recordingError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm mb-2" style={{ color: '#dc2626' }}>
          {recordingError}
        </div>
      )}

      {/* Main Input Card */}
      <div 
        className="rounded-2xl overflow-hidden"
        style={{
          backgroundColor: 'rgb(254, 254, 254)',
          boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.02), 0 -2px 12px -4px rgba(0, 0, 0, 0.02), 0 4px 16px -6px rgba(0, 0, 0, 0.06), 0 8px 20px -10px rgba(0, 0, 0, 0.04)',
          border: '1px solid rgba(0, 0, 0, 0.05)',
          transition: 'border-color 0.2s ease',
        }}
      >
        {/* Header with AI Badge */}
        <div className="flex items-center gap-2 px-5 pt-4 pb-2">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm font-medium">
            <Sparkles className="w-3.5 h-3.5" />
            AI Assistant
          </span>
          <span className="text-gray-500 text-sm">
            {isRecording 
              ? "Recording... Click mic to stop" 
              : isTranscribing 
              ? "Processing voice input..." 
              : "Describe what you need help with"}
          </span>
          {isRecording && (
            <span className="relative flex h-2.5 w-2.5 ml-1">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
            </span>
          )}
        </div>

        {/* Textarea */}
        <div className="px-5 pb-2">
          <textarea
            value={value}
            onChange={(e) => onValueChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isRecording ? "Speak your estimate details..." : placeholder}
            className="w-full min-h-[80px] bg-transparent placeholder:text-gray-400 resize-none focus:outline-none text-base leading-relaxed"
            style={{ color: '#424242' }}
            disabled={isLoading || isRecording || isTranscribing}
          />
        </div>

        {/* Bottom Toolbar */}
        <div className="flex items-center justify-end px-5 py-3 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <motion.button
              className={`rounded-full h-9 p-0 group flex items-center justify-center ${
                isRecording 
                  ? 'bg-red-500 hover:bg-red-600 px-2 gap-1.5' 
                  : 'hover:bg-[#006de2]'
              }`}
              onClick={handleMicClick}
              disabled={isMicDisabled}
              title={isRecording ? "Stop recording" : "Start voice input"}
              animate={{
                width: isRecording ? 72 : 36,
                backgroundColor: isRecording ? '#ef4444' : 'transparent',
              }}
              transition={{
                duration: 0.25,
                ease: [0.4, 0, 0.2, 1],
              }}
              whileHover={{
                backgroundColor: isRecording ? '#dc2626' : '#006de2',
              }}
              style={{ border: 'none', cursor: isMicDisabled ? 'not-allowed' : 'pointer', opacity: isMicDisabled ? 0.5 : 1 }}
            >
              <AnimatePresence mode="wait" initial={false}>
                {isTranscribing ? (
                  <motion.div
                    key="transcribing"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.15 }}
                  >
                    <Loader2 style={{ width: 20, height: 20 }} className="text-gray-500 animate-spin" />
                  </motion.div>
                ) : isRecording ? (
                  <motion.div
                    key="recording"
                    className="flex items-center gap-1.5"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.18, delay: 0.05 }}
                  >
                    <SoundWaveWrapper>
                      {(() => {
                        const isSpeaking = audioLevel > 0.08;
                        const heightMultiplier = Math.min(audioLevel * 8, 1);
                        const baseHeight = 4 + heightMultiplier * 10;
                        const middleHeight = 4 + heightMultiplier * 14;
                        return (
                          <>
                            <SoundBar 
                              className={isSpeaking ? 'speaking bar-1' : ''} 
                              style={{ height: `${baseHeight}px` }} 
                            />
                            <SoundBar 
                              className={isSpeaking ? 'speaking bar-2' : ''} 
                              style={{ height: `${middleHeight}px` }} 
                            />
                            <SoundBar 
                              className={isSpeaking ? 'speaking bar-3' : ''} 
                              style={{ height: `${baseHeight}px` }} 
                            />
                          </>
                        );
                      })()}
                    </SoundWaveWrapper>
                    <Pause 
                      style={{ width: 16, height: 16 }} 
                      className="text-white fill-white"
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key="mic"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.15 }}
                    className="flex items-center justify-center"
                  >
                    <Mic 
                      style={{ width: 20, height: 20 }} 
                      className="text-gray-500 group-hover:text-white transition-colors"
                    />
                  </motion.div>
                )}
              </AnimatePresence>
              <span className="sr-only">{isRecording ? "Stop recording" : "Voice input"}</span>
            </motion.button>
            <GenerateButton
              onClick={onSubmit}
              disabled={isLoading || !value?.trim() || isRecording || isTranscribing}
              isLoading={isLoading}
              hasValue={!!value?.trim()}
            />
          </div>
        </div>
      </div>

      {/* AI Disclaimer */}
      <div className="text-center px-1">
        <span className="text-sm" style={{ color: '#727272', visibility: showDisclaimer ? 'visible' : 'hidden' }}>AI Assistant can make mistakes. Check important info.</span>
      </div>
    </div>
  )
}
